import type { Product, CropData, ProductCategory } from '@/types';
import { ListingOrchestrator } from './ListingOrchestrator';
import { logger } from '@/lib/logger';
import apiClient from '@/lib/apiClient';
import { fetchWithRetry } from '@/lib/fetchWithRetry';

interface DbProduct {
  id: number;
  title: string;
  category: string;
  googleDriveLink: string | null;
  bunnyBackupSlug: string;
  printFileUrls: Record<string, string> | null;
  masterImageUrl: string | null;
  orientation: string | null;
  finalPdfUrl: string | null;
  mockupUrls: string[] | null;
  createdAt: string;
  updatedAt: string;
}

function toProduct(p: DbProduct): Product {
  return {
    id: String(p.id),
    title: p.title,
    category: p.category as ProductCategory,
    googleDriveLink: p.googleDriveLink,
    bunnyBackupSlug: p.bunnyBackupSlug,
    printFileUrls: p.printFileUrls,
    masterImageUrl: p.masterImageUrl,
    orientation: p.orientation as 'portrait' | 'landscape' | null,
    finalPdfUrl: p.finalPdfUrl,
    mockupUrls: p.mockupUrls || [],
    createdAt: new Date(p.createdAt),
    updatedAt: new Date(p.updatedAt),
  };
}

export class ProductService {
  private orchestrator: ListingOrchestrator;

  constructor() {
    this.orchestrator = new ListingOrchestrator();
  }

  async createProduct(
    masterImage: File,
    crops: CropData[],
    title: string,
    category: ProductCategory
  ): Promise<Product> {
    const cropsWithBlobs = await this.orchestrator.generateAssets(masterImage, crops);

    const uploadResult = await this.orchestrator.executeDualUpload(
      cropsWithBlobs,
      title,
      category
    );

    const masterImageUrl = await this.uploadMasterImage(
      masterImage,
      uploadResult.bunny.slug
    );

    const orientation = crops[0]?.orientation || 'portrait';

    const response = await apiClient.post('/products/save', {
      title,
      category,
      googleDriveLink: uploadResult.drive.folderLink,
      bunnyBackupSlug: uploadResult.bunny.slug,
      printFileUrls: uploadResult.bunny.printFileUrls,
      masterImageUrl,
      orientation,
      finalPdfUrl: null,
      mockupUrls: [],
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to save product');
    }

    return toProduct(response.data.data);
  }

  private async uploadMasterImage(file: File, slug: string): Promise<string> {
    const credentials = await this.orchestrator.getBunnyCredentials();
    const { storageZone, storagePassword, pullZone } = credentials;

    const extension = file.name.split('.').pop() || 'jpg';
    const fileName = `${slug}/master.${extension}`;
    const uploadUrl = `https://storage.bunnycdn.com/${storageZone}/${fileName}`;

    const arrayBuffer = await file.arrayBuffer();

    const uploadResponse = await fetchWithRetry(uploadUrl, {
      method: 'PUT',
      headers: {
        'AccessKey': storagePassword,
        'Content-Type': file.type,
      },
      body: arrayBuffer,
      timeout: 60000,
      maxRetries: 3,
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload master image to BunnyCDN');
    }

    return `https://${pullZone}/${fileName}`;
  }

  async getProduct(id: string): Promise<Product | null> {
    try {
      const response = await apiClient.get(`/products/${id}`);
      if (!response.data.success) return null;

      return toProduct(response.data.data);
    } catch (error) {
      logger.error('ProductService', 'Failed to get product', error);
      return null;
    }
  }

  async deleteProduct(id: string): Promise<boolean> {
    try {
      const response = await apiClient.delete(`/products/${id}`);
      return response.data.success;
    } catch (error) {
      logger.error('ProductService', 'Failed to delete product', error);
      return false;
    }
  }

  async updateProduct(id: string, updates: Partial<Product>): Promise<Product | null> {
    try {
      const response = await apiClient.patch(`/products/${id}`, updates);
      if (!response.data.success) return null;

      return toProduct(response.data.data);
    } catch (error) {
      logger.error('ProductService', 'Failed to update product', error);
      return null;
    }
  }
}
